<template>
  <g class="AxisTick" :transform="transform" :ref="index + orient">
    <line
      v-if="!hideLines"
      class="AxisTick-tickLine"
      :stroke="strokeColor"
      :x2="yGrid ? width : x2"
      :y2="xGrid ? -height : y2"
      :transform="tickRotation"
      :stroke-dasharray="strokeDasharray"
    >
      <title>{{ label }}</title>
    </line>
    <g :transform="`translate(${labelX}, ${labelY})`">
      <text
        v-if="label && !hideLabels"
        class="AxisTick-label"
        fill="#606060"
        :dy="dy"
        :transform="labelRotation"
        :text-anchor="textAnchor"
      >
        <title>{{ label }}</title>{{ formattedLabel }}
      </text>
    </g>
  </g>
</template>

<script>
import { useElementSize } from '@vueuse/core';

export default {
  name: 'DatalasAxisTick',

  emits: ['update:axisTickSize'],

  props: {
    transform: {
      type: String,
    },
    x2: {
      type: Number,
    },
    y2: {
      type: Number,
    },
    x: {
      type: Number,
    },
    y: {
      type: Number,
    },
    dy: {
      // TODO: deprecate, in favor of labelOffset?
      type: String,
    },
    label: {
      type: String,
      default: '',
    },
    strokeColor: {
      type: String,
      default: '#000',
    },
    labelAngle: {
      type: Number,
    },
    labelOffset: {
      type: Number,
      default: 3,
    },
    tickAngle: {
      type: Number,
    },
    orient: {
      type: String,
    },
    width: {
      type: Number,
    },
    height: {
      type: Number,
    },
    xGrid: {
      type: Boolean,
    },
    yGrid: {
      type: Boolean,
    },
    left: Number,

    countCharsToStartTruncate: Number,
    autoTruncate: Boolean,
    index: Number,

    strokeDasharray: { type: Number, default: 0 },
    hideLines: Boolean,
    hideLabels: Boolean,
  },

  computed: {
    isVertical() {
      return this.orient === 'left' || this.orient === 'right';
    },
    isBackwards() {
      return this.orient === 'top' || this.orient === 'right';
    },
    tickRadians() {
      return this.tickAngle * (Math.PI / 180);
    },
    tickLength() {
      return (
        Math.abs((this.isVertical ? this.x2 : this.y2) || 0) + this.labelOffset
      );
    },
    labelX() {
      const method = this.isVertical ? 'cos' : 'sin';
      const f = this.isBackwards ? 1 : -1;
      return Math[method](this.tickRadians) * this.tickLength * f;
    },
    labelY() {
      const method = this.isVertical ? 'sin' : 'cos';
      const f = this.orient === 'right' || this.orient === 'bottom' ? 1 : -1;
      return Math[method](this.tickRadians) * this.tickLength * f;
    },
    labelRotation() {
      if (!this.labelAngle) return undefined;
      return `rotate(${this.labelAngle})`;
    },
    formattedLabel() {
      if (this.autoTruncate) {
        const maxCountSymbols = Math.floor((this.left - this.labelOffset) / 11);
        return this.label.length > maxCountSymbols
          ? `${this.label.substring(
              0,
              Math.floor((this.left - this.labelOffset - 12.52) / 11),
            )}...`
          : this.label;
      }

      return this.countCharsToStartTruncate
        ? this.label.slice(0, this.countCharsToStartTruncate) +
            (this.label.length > this.countCharsToStartTruncate ? '...' : '')
        : this.label;
    },
    tickRotation() {
      return (this.tickAngle || undefined) && `rotate(${this.tickAngle})`;
    },
    textAnchor() {
      if (!this.labelAngle) return undefined;

      if (this.isVertical) return this.isBackwards ? 'start' : 'end';
      if (this.isBackwards) return this.labelAngle > 0 ? 'end' : 'start';
      return this.labelAngle > 0 ? 'start' : 'end';
    },
    elementSize() {
      const { height: axisTickHeight, width: axisTickWidth } = useElementSize(
        this.$refs[this.index + this.orient],
      );
      return {
        id: this.index + this.orient,
        orient: this.orient,
        height: axisTickHeight,
        width: axisTickWidth,
      };
    },
  },
  mounted() {
    if (this.elementSize) {
      this.$emit('update:axisTickSize', this.elementSize);
    }
  },
};
</script>
